/**
 * Client-side error logging to Firebase
 */

import { buildAppsationsFunctionUrl } from "@/lib/appsationsSdkEndpoints";

interface ErrorPayload {
  type: string;
  message: string;
  stack?: string;
  context?: Record<string, unknown>;
  url?: string;
  userAgent?: string;
  timestamp: number;
}

const runtimeLogEnabled =
  typeof import.meta !== "undefined" &&
  (import.meta.env as { VITE_ENABLE_RUNTIME_LOGGER?: string }).VITE_ENABLE_RUNTIME_LOGGER === "1";

const logToFirebase = async (payload: ErrorPayload): Promise<void> => {
  if (!runtimeLogEnabled) return;
  try {
    const runtimeErrorUrl = buildAppsationsFunctionUrl("logRuntimeError");
    // Use sendBeacon for reliability (works even during page unload)
    const blob = new Blob([JSON.stringify(payload)], {
      type: "application/json",
    });
    
    if (navigator.sendBeacon) {
      navigator.sendBeacon(runtimeErrorUrl, blob);
    } else {
      // Fallback to fetch with keepalive
      fetch(runtimeErrorUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(() => {
        // Ignore fetch errors in error logger
      });
    }
  } catch {
    // Silently fail - don't want error logger to cause more errors
  }
};

export const logError = (
  type: string,
  message: string,
  stack?: string,
  context?: Record<string, unknown>
): void => {
  const payload: ErrorPayload = {
    type,
    message,
    stack,
    context,
    url: window.location.href,
    userAgent: navigator.userAgent,
    timestamp: Date.now(),
  };

  console.error(`[ErrorLogger] ${type}:`, message, context);
  logToFirebase(payload);
};

export const initErrorLogger = (): void => {
  // Catch unhandled errors
  window.addEventListener("error", (event) => {
    logError(
      "unhandled-error",
      event.message,
      event.error?.stack,
      {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      }
    );
  });

  // Catch unhandled promise rejections
  window.addEventListener("unhandledrejection", (event) => {
    logError(
      "unhandled-rejection",
      event.reason?.message || String(event.reason),
      event.reason?.stack,
      {
        promise: event.promise.toString(),
      }
    );
  });
};

export const logStorageIssue = (
  operation: string,
  success: boolean,
  details?: Record<string, unknown>
): void => {
  logError(
    "storage-issue",
    `iOS Storage ${operation}: ${success ? "SUCCESS" : "FAILED"}`,
    undefined,
    {
      operation,
      success,
      ...details,
      localStorage: {
        available: typeof localStorage !== "undefined",
        testWrite: (() => {
          try {
            localStorage.setItem("__test__", "1");
            localStorage.removeItem("__test__");
            return true;
          } catch {
            return false;
          }
        })(),
      },
      sessionStorage: {
        available: typeof sessionStorage !== "undefined",
        testWrite: (() => {
          try {
            sessionStorage.setItem("__test__", "1");
            sessionStorage.removeItem("__test__");
            return true;
          } catch {
            return false;
          }
        })(),
      },
    }
  );
};
